// Shared projection registry — faithful TS port of pipeline/georef/models.py.
//
// Every supported map projection reduces, at the pixel level, to a two-stage
// pipeline used identically by the Python fitter, the production overlay and
// the calibration tool:
//
//     (lon,lat) --geoToPlanar(uProjModel,params)--> (X,Y) --affine 2x3--> (sx,sy)
//
// `geoToPlanar` carries ONLY the intrinsic projection shape. The placement,
// scale, skew and false easting/northing onto the pixel grid are entirely
// absorbed by the least-squares affine A (exactly as `solve_affine` does in
// models.py). This keeps the pixel mapping a single shared mechanism across all
// families, and — crucially — makes the equirectangular path (uProjModel=0)
// byte-identical to the historical lon/lat affine (X=lon, Y=lat), so the
// production map is unchanged when this module is wired into pollenLayer.
//
// uProjModel registry (shared TS/Python contract):
//   0  equirectangular            X=lon, Y=lat                       (none)
//   1  web_mercator               X=lon, Y=ln(tan(pi/4+lat/2))       (none)
//   2  lambert_conformal_conic    conic                              lon0,lat0,lat1,lat2
//   3  mesh_lookup (poly2/TPS)    baked lookup — NOT slider-tunable  (lookup blob)
//   4  etrs89_laea_3035           Lambert Azimuthal Equal-Area       lon0,lat0
//   5  stereographic              oblique/polar stereographic        lon0,lat0,k0,mode

export const DEG = Math.PI / 180;

// Tunable projection families (3 = mesh lookup is bake-only, not exposed here).
export type ProjId = 0 | 1 | 2 | 4 | 5;

export type StereoMode = "oblique" | "polar_north" | "polar_south";

export interface ProjParams {
  lon0?: number; // central meridian (deg)
  lat0?: number; // latitude of origin / projection centre (deg)
  lat1?: number; // LCC standard parallel 1 (deg)
  lat2?: number; // LCC standard parallel 2 (deg)
  k0?: number; // stereographic scale at centre
  mode?: StereoMode; // stereographic centre mode
}

export type Affine = [[number, number, number], [number, number, number]];

// --- family metadata ------------------------------------------------------

export const PROJ_IDS: ProjId[] = [0, 1, 2, 4, 5];

export const PROJ_LABELS: Record<ProjId, string> = {
  0: "Equirectangular (plate-carrée)",
  1: "Web Mercator (EPSG:3857)",
  2: "Lambert Conformal Conic",
  4: "ETRS89-LAEA (EPSG:3035)",
  5: "Stereographic (oblique/polar)",
};

// String family id stored in the manifest geo block (`selected_model`).
export const PROJ_MODEL_ID: Record<ProjId, string> = {
  0: "equirectangular",
  1: "web_mercator",
  2: "lambert_conformal_conic",
  4: "etrs89_laea_3035",
  5: "stereographic",
};

// EPSG:3035 official origin is the default for LAEA; the others default to a
// central-Europe centre so the user starts near-correct for the EAN raster.
export const PROJ_DEFAULTS: Record<ProjId, ProjParams> = {
  0: {},
  1: {},
  2: { lon0: 15, lat0: 50, lat1: 40, lat2: 60 },
  4: { lon0: 10, lat0: 52 },
  5: { lon0: 10, lat0: 52, k0: 1, mode: "oblique" },
};

export interface ParamControl {
  key: keyof ProjParams;
  label: string;
  min: number;
  max: number;
  step: number;
  default: number;
}

// Slider schema per family (numeric params only; `mode` is a separate toggle).
export const PROJ_PARAM_SCHEMA: Record<ProjId, ParamControl[]> = {
  0: [],
  1: [],
  2: [
    { key: "lon0", label: "Central meridian λ₀", min: -30, max: 50, step: 0.1, default: 15 },
    { key: "lat0", label: "Latitude of origin φ₀", min: 20, max: 80, step: 0.1, default: 50 },
    { key: "lat1", label: "Standard parallel 1", min: 20, max: 80, step: 0.1, default: 40 },
    { key: "lat2", label: "Standard parallel 2", min: 20, max: 80, step: 0.1, default: 60 },
  ],
  4: [
    { key: "lon0", label: "Central meridian λ₀", min: -30, max: 50, step: 0.1, default: 10 },
    { key: "lat0", label: "Latitude of origin φ₀", min: 20, max: 80, step: 0.1, default: 52 },
  ],
  5: [
    { key: "lon0", label: "Central meridian λ₀", min: -30, max: 50, step: 0.1, default: 10 },
    { key: "lat0", label: "Latitude of origin φ₀", min: -90, max: 90, step: 0.1, default: 52 },
    { key: "k0", label: "Scale at centre k₀", min: 0.5, max: 2, step: 0.001, default: 1 },
  ],
};

// --------------------------------------------------------------------------
// Forward: geo (lon,lat in deg) -> planar (X,Y, dimensionless/projected)
// --------------------------------------------------------------------------

function mercY(latDeg: number): number {
  const lat = Math.max(-85.06, Math.min(85.06, latDeg)) * DEG;
  return Math.log(Math.tan(Math.PI / 4 + lat / 2));
}

// LCC intrinsic constants n, F, rho0 from the standard parallels + origin.
function lccConstants(p: ProjParams): { n: number; F: number; rho0: number } {
  const lat1 = (p.lat1 ?? 40) * DEG;
  const lat2 = (p.lat2 ?? 60) * DEG;
  const lat0 = (p.lat0 ?? 50) * DEG;
  let n: number;
  if (Math.abs(lat1 - lat2) < 1e-9) {
    n = Math.sin(lat1);
  } else {
    n =
      Math.log(Math.cos(lat1) / Math.cos(lat2)) /
      Math.log(
        Math.tan(Math.PI / 4 + lat2 / 2) / Math.tan(Math.PI / 4 + lat1 / 2),
      );
  }
  const F = (Math.cos(lat1) * Math.pow(Math.tan(Math.PI / 4 + lat1 / 2), n)) / n;
  const rho0 = F / Math.pow(Math.tan(Math.PI / 4 + lat0 / 2), n);
  return { n, F, rho0 };
}

// Effective latitude of origin for a stereographic family, honouring `mode`.
function stereoLat0(p: ProjParams): number {
  switch (p.mode) {
    case "polar_north":
      return 90;
    case "polar_south":
      return -90;
    default:
      return p.lat0 ?? 52;
  }
}

export function geoToPlanar(
  id: ProjId,
  p: ProjParams,
  lon: number,
  lat: number,
): [number, number] {
  switch (id) {
    case 0:
      return [lon, lat];
    case 1:
      return [lon, mercY(lat)];
    case 2: {
      const { n, F, rho0 } = lccConstants(p);
      const lon0 = (p.lon0 ?? 15) * DEG;
      const phi = Math.max(-89.9, Math.min(89.9, lat)) * DEG;
      const rho = F / Math.pow(Math.tan(Math.PI / 4 + phi / 2), n);
      const theta = n * (lon * DEG - lon0);
      return [rho * Math.sin(theta), rho0 - rho * Math.cos(theta)];
    }
    case 4: {
      const lon0 = (p.lon0 ?? 10) * DEG;
      const lat0 = (p.lat0 ?? 52) * DEG;
      const phi = lat * DEG;
      const lam = lon * DEG;
      const denom =
        1 +
        Math.sin(lat0) * Math.sin(phi) +
        Math.cos(lat0) * Math.cos(phi) * Math.cos(lam - lon0);
      const kp = Math.sqrt(2 / Math.max(denom, 1e-12));
      const X = kp * Math.cos(phi) * Math.sin(lam - lon0);
      const Y =
        kp *
        (Math.cos(lat0) * Math.sin(phi) -
          Math.sin(lat0) * Math.cos(phi) * Math.cos(lam - lon0));
      return [X, Y];
    }
    case 5: {
      const lon0 = (p.lon0 ?? 10) * DEG;
      const lat0 = stereoLat0(p) * DEG;
      const k0 = p.k0 ?? 1;
      const phi = lat * DEG;
      const lam = lon * DEG;
      const denom =
        1 +
        Math.sin(lat0) * Math.sin(phi) +
        Math.cos(lat0) * Math.cos(phi) * Math.cos(lam - lon0);
      const k = (2 * k0) / Math.max(denom, 1e-12);
      const X = k * Math.cos(phi) * Math.sin(lam - lon0);
      const Y =
        k *
        (Math.cos(lat0) * Math.sin(phi) -
          Math.sin(lat0) * Math.cos(phi) * Math.cos(lam - lon0));
      return [X, Y];
    }
    default:
      return [lon, lat];
  }
}

// --------------------------------------------------------------------------
// Inverse: planar (X,Y) -> geo (lon,lat in deg). Used in-tool to recompute the
// bbox corners from the affine so the exported corners stay consistent with A.
// --------------------------------------------------------------------------

export function planarToGeo(
  id: ProjId,
  p: ProjParams,
  X: number,
  Y: number,
): [number, number] {
  switch (id) {
    case 0:
      return [X, Y];
    case 1:
      return [X, (2 * Math.atan(Math.exp(Y)) - Math.PI / 2) / DEG];
    case 2: {
      const { n, F, rho0 } = lccConstants(p);
      const lon0 = p.lon0 ?? 15;
      const rho = Math.sign(n) * Math.sqrt(X * X + (rho0 - Y) * (rho0 - Y));
      const theta = Math.atan2(X, rho0 - Y);
      const lon = lon0 + theta / n / DEG;
      const lat =
        (2 * Math.atan(Math.pow(F / rho, 1 / n)) - Math.PI / 2) / DEG;
      return [lon, lat];
    }
    case 4: {
      const lon0 = (p.lon0 ?? 10) * DEG;
      const lat0 = (p.lat0 ?? 52) * DEG;
      const rho = Math.sqrt(X * X + Y * Y);
      if (rho < 1e-12) return [lon0 / DEG, lat0 / DEG];
      const c = 2 * Math.asin(Math.max(-1, Math.min(1, rho / 2)));
      const lat =
        Math.asin(
          Math.max(
            -1,
            Math.min(
              1,
              Math.cos(c) * Math.sin(lat0) +
                (Y * Math.sin(c) * Math.cos(lat0)) / rho,
            ),
          ),
        ) / DEG;
      const lon =
        (lon0 +
          Math.atan2(
            X * Math.sin(c),
            rho * Math.cos(lat0) * Math.cos(c) - Y * Math.sin(lat0) * Math.sin(c),
          )) /
        DEG;
      return [lon, lat];
    }
    case 5: {
      const lon0 = (p.lon0 ?? 10) * DEG;
      const lat0 = stereoLat0(p) * DEG;
      const k0 = p.k0 ?? 1;
      const rho = Math.sqrt(X * X + Y * Y);
      if (rho < 1e-12) return [lon0 / DEG, lat0 / DEG];
      const c = 2 * Math.atan(rho / (2 * k0));
      const lat =
        Math.asin(
          Math.max(
            -1,
            Math.min(
              1,
              Math.cos(c) * Math.sin(lat0) +
                (Y * Math.sin(c) * Math.cos(lat0)) / rho,
            ),
          ),
        ) / DEG;
      const lon =
        (lon0 +
          Math.atan2(
            X * Math.sin(c),
            rho * Math.cos(lat0) * Math.cos(c) - Y * Math.sin(lat0) * Math.sin(c),
          )) /
        DEG;
      return [lon, lat];
    }
    default:
      return [X, Y];
  }
}
